import { Component, ElementRef, Input, OnInit, Renderer2 } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, shareReplay, take, tap } from 'rxjs/operators';
import {
  AHevchStrategyEnum,
  ChangePlantConfigInput,
  CmdAzzurroHubService,
  isNotNullOrUndefined,
  System,
} from 'ngx-core-services';
import { ToastFacade } from 'ngx-zcs-components';

import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { selectedSystemAzzurroHubData, SystemsState } from '../../store';

const getLabels = () => ({
  title: 'EV Charge Configuration',
  description: 'Configure how the EV charger interacts with the solar installation, battery storage, and grid power.',
  enableControl: 'Enable EV Charge Control',
  enableControlDesc: 'When enabled, the EV charge control system becomes active and manages charging based on the configured parameters below.',

  timeSectionTitle: 'Time Slot Configuration',
  timeSectionDescription: 'Schedule charging to occur only within specific time windows. Optimize charging during off-peak hours or when solar production is highest.',
  enableTimeMode: 'Enable Time Slot Mode',
  timeStart: 'Start Time',
  timeStartDescription: 'Beginning of the charging time window. Charging will only occur after this time.',
  timeStartPlaceholder: '08:00',
  timeStop: 'End Time',
  timeStopDescription: 'End of the charging time window. Charging will stop at this time.',
  timeStopPlaceholder: '18:00',
  timeFormatError: 'Please enter a valid time (HH:mm)',

  batterySectionTitle: 'Battery Protection',
  batterySectionDescription: 'Protect home battery storage from being depleted by EV charging. Ensure sufficient battery reserve for home consumption.',
  enableBatteryProtect: 'Enable Battery Protection',
  batterySOCLimit: 'Battery SOC Limit (%)',
  batterySOCLimitDescription: 'The battery will not be used for EV charging when State of Charge (SOC) falls below this percentage. Set to 98% to effectively exclude the battery from charging entirely.',
  batterySOCLimitPlaceholder: '30',
  batterySOCError: 'Value must be between 1 and 95',

  powerSectionTitle: 'Grid Power Limit',
  powerSectionDescription: 'Control how much power can be imported from the grid for EV charging. Limit grid dependency and maximize renewable energy usage.',
  enablePowerImport: 'Limit Grid Power',
  powerImportValue: 'Max Grid Power (%)',
  powerImportValueDescription: 'Maximum percentage of power that can be imported from the grid for charging. Set to 100 for fast charging mode (unrestricted grid usage).',
  powerImportValuePlaceholder: '50',
  powerImportError: 'Value must be between 1 and 99',

  enableParallel: 'Enable Parallel Charging',
  enableParallelDesc: 'Enable management of multiple EV chargers simultaneously. Power is intelligently distributed across multiple connected chargers to ensure efficient charging and prevent grid overload.',

  selectedFastTitle: 'Fast Charging Mode Selected',
  selectedFastDescription: 'Fast Charging Mode is selected. This mode allows the EV charger to draw maximum power from the grid without restrictions, ensuring the quickest possible charging times.',
  selectedGreenTitle: 'Green Charging Mode Selected',
  selectedGreenDescription: 'Green Charging Mode is selected. This mode optimizes EV charging to prioritize the use of renewable energy sources, such as solar power, and minimizes grid consumption. This may result in longer charging times but supports sustainability and can reduce electricity costs.',

  save: 'Save Configuration',
  reset: 'Reset',
  saving: 'Saving configuration...',
  saveSuccess: 'Configuration saved successfully',
  saveError: 'Failed to save configuration',
  loading: 'Loading configuration...',
});

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({ 'breakpoint-550': 550 })
@Component({
  selector: 'lib-evcharge-config',
  templateUrl: './evcharge-config.component.html',
  styleUrls: ['./evcharge-config.component.scss'],
})
export class EvchargeConfigComponent implements IReduxBlock, ICustomBlock {
  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  get hubId(): string | undefined {
    return this.system$.value?.configuration?.azzurroHubId;
  }

  readonly _instanceId$: Observable<string> = new Observable<string>();

  form = new FormGroup({
    evchCtrlEna: new FormControl(false),
    evchTimeModeEna: new FormControl(false),
    evchTimeStart: new FormControl(''),
    evchTimeStop: new FormControl(''),
    evchBatteryProtectEna: new FormControl(false),
    evchBatterySOCLimit: new FormControl<number | null>(null),
    evchPwrImportLimit: new FormControl(false),
    evchPwrImportValue: new FormControl<number | null>(null),
    evchParallelEna: new FormControl(false),
  });

  readonly config$ = this.store.select(selectedSystemAzzurroHubData).pipe(
    isNotNullOrUndefined(),
    map((data) => data?.config),
    tap((config) => {
      if (config) {
        this.form.patchValue({
          evchCtrlEna: config.evchCtrlEna,
          evchTimeModeEna: config.evchTimeModeEna,
          evchTimeStart: config.evchTimeStart || '',
          evchTimeStop: config.evchTimeStop || '',
          evchBatteryProtectEna: config.evchBatteryProtectEna,
          evchBatterySOCLimit: config.evchBatterySOCLimit,
          evchPwrImportLimit: config.evchPwrImportLimit,
          evchPwrImportValue: config.evchPwrImportValue,
          evchParallelEna: config.evchParallelEna,
        });
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  AHevchStrategyEnum = AHevchStrategyEnum;

  constructor(
    readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
    private readonly cmdAzzurroHubService: CmdAzzurroHubService,
    private readonly toastFacade: ToastFacade,
  ) {}

  async onSave() {
    if (this.form.invalid || !this.hubId) {
      return;
    }

    const formValue = this.form.value;
    const config: ChangePlantConfigInput = {
      evchCtrlEna: formValue.evchCtrlEna || false,
      evchTimeModeEna: formValue.evchTimeModeEna || false,
      evchTimeStart: formValue.evchTimeModeEna ? formValue.evchTimeStart || undefined : undefined,
      evchTimeStop: formValue.evchTimeModeEna ? formValue.evchTimeStop || undefined : undefined,
      evchBatteryProtectEna: formValue.evchBatteryProtectEna || false,
      evchBatterySOCLimit: formValue.evchBatteryProtectEna ? formValue.evchBatterySOCLimit || undefined : undefined,
      evchPwrImportLimit: formValue.evchPwrImportLimit || false,
      evchPwrImportValue: formValue.evchPwrImportLimit ? formValue.evchPwrImportValue || undefined : undefined,
      evchParallelEna: formValue.evchParallelEna || false,
    };

    try {
      const result = await this.cmdAzzurroHubService.configureEvcharge(
        this.hubId,
        config,
        this.labels.saving
      );

      if (result.error) {
        await this.toastFacade.present(this.labels.saveError, undefined, {
          severity: 'error',
          mobileMode: 'single',
          browserMode: 'simple'
        });
      } else if (result.success) {
        await this.toastFacade.present(this.labels.saveSuccess, undefined, {
          severity: 'success',
          mobileMode: 'single',
          browserMode: 'simple'
        });
      }
    } catch (error) {
      await this.toastFacade.present(this.labels.saveError, undefined, {
        severity: 'error',
        mobileMode: 'single',
        browserMode: 'simple'
      });
    }
  }

  async loadConfiguration() {
    const currentConfig = await this.config$.pipe(take(1)).toPromise();
    if (!currentConfig) {
      return;
    }
    this.form.patchValue({
      evchCtrlEna: currentConfig.evchCtrlEna,
      evchTimeModeEna: currentConfig.evchTimeModeEna,
      evchTimeStart: currentConfig.evchTimeStart || '',
      evchTimeStop: currentConfig.evchTimeStop || '',
      evchBatteryProtectEna: currentConfig.evchBatteryProtectEna,
      evchBatterySOCLimit: currentConfig.evchBatterySOCLimit,
      evchPwrImportLimit: currentConfig.evchPwrImportLimit,
      evchPwrImportValue: currentConfig.evchPwrImportValue,
      evchParallelEna: currentConfig.evchParallelEna,
    });
  }

  async onReset() {
    await this.loadConfiguration();
  }
}
